import { z } from 'zod';

/**
 * Optimistische Sperre für den Beleg-Editor (ADR 0115). Der Editor speichert
 * automatisch kurz nach jeder Änderung und schickt dabei den Stand mit, auf dem
 * die Eingabe beruht (`updatedAt` des Belegs). Hat jemand den Beleg inzwischen
 * an anderer Stelle gespeichert — ein zweites Fenster, ein Kollege, ein
 * Statuswechsel —, lehnt der Server mit 409 ab, statt die fremde Änderung still
 * zu überschreiben.
 *
 * Weglassen = Schreiben ohne Prüfung (andere Aufrufer, „Meine Fassung speichern").
 */
export const expectedUpdatedAtSchema = z.string().datetime().optional();

/**
 * RFC-7807-`title` der 409-Antwort bei veraltetem Stand. Unterscheidet sie von
 * den übrigen 409 derselben Endpunkte (Absenderwechsel nicht mehr möglich,
 * Status nicht Entwurf …): nur hier zeigt der Editor „Neu laden / Meine Fassung
 * speichern".
 */
export const DOCUMENT_STALE_CONFLICT_TITLE = 'Beleg inzwischen geändert';

/** Meldung (`detail`) der 409-Antwort bei veraltetem Stand. */
export function documentStaleConflictMessage(number: string): string {
  return (
    `Der Beleg ${number} wurde inzwischen an anderer Stelle geändert (anderes Fenster oder andere Person). ` +
    'Deine Änderung wurde nicht gespeichert, damit nichts überschrieben wird — bitte neu laden oder ' +
    'bewusst die eigene Fassung speichern.'
  );
}

/**
 * Ist der gespeicherte Stand neuer (oder älter) als der, auf dem die Eingabe
 * beruht? Verglichen wird in Millisekunden — so liefert die API `updatedAt` aus.
 * Ohne `expected` gibt es nichts zu prüfen.
 */
export function isStaleDocumentVersion(actual: Date | string, expected: string | undefined | null): boolean {
  if (!expected) return false;
  const actualMs = typeof actual === 'string' ? Date.parse(actual) : actual.getTime();
  return actualMs !== Date.parse(expected);
}
